import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, Copy, Clock, MapPin, Instagram, Check, Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Us | Coyote Creek Hat Co." },
      { name: "description", content: "Get in touch with Coyote Creek Hat Co. — questions, orders, custom requests, and partnerships. Real people, real replies." },
      { property: "og:title", content: "Contact Us | Coyote Creek Hat Co." },
      { property: "og:description", content: "Real people, real replies. Usually within one business day." },
      { property: "og:url", content: "https://coyotecreekhatco.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/contact" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Coyote Creek Hat Co.",
        url: "https://coyotecreekhatco.com",
        image: "https://storage.googleapis.com/gpt-engineer-file-uploads/SzMe6IFZhrZ7NgTJI79ZOppiF9b2/social-images/social-1779766489683-2026_Website_Logo.webp",
        email: "contact@coyotecreekhatco.com",
        address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        }],
        sameAs: [
          "https://www.instagram.com/coyotecreekhatco/",
          "https://www.facebook.com/coyotecreekhatco/",
        ],
      }),
    }],
  }),
  component: ContactPage,
});

const TOPICS = ["Order Help", "Custom Hats", "Wholesale", "Press", "Just Saying Hi"] as const;
type Topic = typeof TOPICS[number];

const TOPIC_INBOX: Record<Topic, string> = {
  "Order Help": "contact@coyotecreekhatco.com",
  "Custom Hats": "gwestcustoms@coyotecreekhatco.com",
  "Wholesale": "wholesale@coyotecreekhatco.com",
  "Press": "contact@coyotecreekhatco.com",
  "Just Saying Hi": "contact@coyotecreekhatco.com",
};

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be under 100 characters"),
  email: z.string().trim().email("Enter a valid email").max(255, "Email must be under 255 characters"),
  topic: z.enum(TOPICS, { error: () => "Pick a topic" }),
  orderNumber: z.string().trim().max(50, "Order # must be under 50 characters").optional(),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be under 1000 characters"),
});

const DIRECT_CHANNELS = [
  { title: "General", email: "contact@coyotecreekhatco.com", note: "Questions, orders, anything else" },
  { title: "Custom Orders", email: "gwestcustoms@coyotecreekhatco.com", note: "Logos, branded runs, gifts" },
  { title: "Wholesale", email: "wholesale@coyotecreekhatco.com", note: "Retailers and resellers" },
];

function ContactPage() {
  const [topic, setTopic] = useState<Topic | "">("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ topic: Topic; inbox: string } | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const handleChipClick = (t: Topic) => {
    setTopic(t);
    setErrors(prev => ({ ...prev, topic: "" }));
    setTimeout(() => messageRef.current?.focus(), 50);
  };

  const handleCopy = async (addr: string) => {
    try {
      await navigator.clipboard.writeText(addr);
      setCopiedEmail(addr);
      toast.success("Email copied");
      setTimeout(() => setCopiedEmail(null), 1500);
    } catch {
      toast.error("Couldn't copy — try again");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = contactSchema.safeParse({ name, email, topic, orderNumber: orderNumber || undefined, message });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as string;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted({ topic: parsed.data.topic, inbox: TOPIC_INBOX[parsed.data.topic] });
    }, 600);
  };

  const handleReset = () => {
    setSubmitted(null);
    setName("");
    setEmail("");
    setTopic("");
    setOrderNumber("");
    setMessage("");
  };

  const charCount = message.length;
  const charMax = 1000;

  return (
    <div>
      {/* Hero */}
      <div className="relative w-full h-[50vh] min-h-[320px] overflow-hidden">
        <img src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/17F1D27E-F8B2-400E-B39F-5A750E95452D.heic?v=1708107655" alt="Contact Us" className="absolute inset-0 h-full w-full object-cover" width={1920} height={768} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white drop-shadow-lg">Contact Us</h1>
        </div>
      </div>

      {/* Reason chips */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            <span className="font-ui text-xs uppercase tracking-widest text-muted-foreground mr-2">What's it about?</span>
            {TOPICS.map(t => {
              const active = topic === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleChipClick(t)}
                  className={`rounded-full border px-4 py-2 font-ui text-sm transition-colors ${
                    active
                      ? "border-blaze bg-blaze text-[#F5EFE2]"
                      : "border-border bg-card text-primary hover:border-blaze hover:text-blaze"
                  }`}
                  aria-pressed={active}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form + Direct channels */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 md:grid-cols-5">
            {/* Form / Success */}
            <div className="md:col-span-3">
              {submitted ? (
                <div
                  role="status"
                  className="rounded-2xl border border-border bg-card p-8 md:p-10"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blaze/10 text-blaze">
                    <Check className="h-6 w-6" />
                  </div>
                  <h2 className="mt-6 font-heading text-3xl text-primary">Thanks — we got it.</h2>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    Your note about <span className="font-medium text-primary">{submitted.topic}</span> is on its way to{" "}
                    <span className="font-medium text-primary">{submitted.inbox}</span>. Expect a reply within one business day.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button type="button" onClick={handleReset} className="btn-cc-primary tracking-[0.2em]">Send another message</button>
                    <Link to="/" className="btn-cc-accent tracking-[0.2em]">Back to home</Link>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-2xl border border-border bg-card p-6 md:p-8"
                  noValidate
                >
                  <h2 className="font-heading text-3xl text-primary">Send us a message</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Fill this out and we'll route it to the right inbox.
                  </p>

                  <div className="mt-8 grid gap-5">
                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <Label htmlFor="contact-name">Name</Label>
                        <Input
                          id="contact-name"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          maxLength={100}
                          aria-invalid={!!errors.name}
                          aria-describedby={errors.name ? "contact-name-err" : undefined}
                          className="mt-2"
                        />
                        {errors.name && (
                          <p id="contact-name-err" className="mt-1 text-xs text-destructive">{errors.name}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="contact-email">Email</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          maxLength={255}
                          aria-invalid={!!errors.email}
                          aria-describedby={errors.email ? "contact-email-err" : undefined}
                          className="mt-2"
                        />
                        {errors.email && (
                          <p id="contact-email-err" className="mt-1 text-xs text-destructive">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-topic">Topic</Label>
                      <Select value={topic} onValueChange={(v) => setTopic(v as Topic)}>
                        <SelectTrigger
                          id="contact-topic"
                          aria-invalid={!!errors.topic}
                          aria-describedby={errors.topic ? "contact-topic-err" : undefined}
                          className="mt-2"
                        >
                          <SelectValue placeholder="Pick a topic" />
                        </SelectTrigger>
                        <SelectContent>
                          {TOPICS.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.topic && (
                        <p id="contact-topic-err" className="mt-1 text-xs text-destructive">{errors.topic}</p>
                      )}
                    </div>

                    {topic === "Order Help" && (
                      <div>
                        <Label htmlFor="contact-order">Order # <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Input
                          id="contact-order"
                          value={orderNumber}
                          onChange={e => setOrderNumber(e.target.value)}
                          maxLength={50}
                          placeholder="e.g. CC-10234"
                          aria-invalid={!!errors.orderNumber}
                          aria-describedby={errors.orderNumber ? "contact-order-err" : undefined}
                          className="mt-2"
                        />
                        {errors.orderNumber && (
                          <p id="contact-order-err" className="mt-1 text-xs text-destructive">{errors.orderNumber}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="contact-message">Message</Label>
                        <span className={`font-ui text-xs ${charCount > charMax ? "text-destructive" : "text-muted-foreground"}`}>
                          {charCount} / {charMax}
                        </span>
                      </div>
                      <Textarea
                        id="contact-message"
                        ref={messageRef}
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        maxLength={charMax}
                        rows={6}
                        aria-invalid={!!errors.message}
                        aria-describedby={errors.message ? "contact-message-err" : undefined}
                        className="mt-2 resize-y"
                      />
                      {errors.message && (
                        <p id="contact-message-err" className="mt-1 text-xs text-destructive">{errors.message}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full btn-cc-accent tracking-[0.2em]"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Direct channels */}
            <aside className="md:col-span-2">
              <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
                <h3 className="font-heading text-2xl text-primary">Direct channels</h3>
                <p className="mt-2 text-sm text-muted-foreground">Prefer email? Reach the right team directly.</p>

                <ul className="mt-6 space-y-4">
                  {DIRECT_CHANNELS.map(c => {
                    const isCopied = copiedEmail === c.email;
                    return (
                      <li key={c.title} className="rounded-xl border border-border bg-background p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blaze/10 text-blaze">
                            <Mail className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-ui text-xs uppercase tracking-widest text-muted-foreground">{c.title}</div>
                            <a href={`mailto:${c.email}`} className="block text-[11px] md:text-xs font-medium text-primary hover:text-blaze break-all leading-snug tracking-tight">
                              {c.email}
                            </a>
                            <div className="mt-1 text-xs text-muted-foreground">{c.note}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopy(c.email)}
                            aria-label={`Copy ${c.title} email`}
                            className="shrink-0 rounded-md border border-border bg-card p-2 text-primary hover:border-blaze hover:text-blaze transition-colors"
                          >
                            {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-6 space-y-3 border-t border-border pt-6">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 text-blaze" />
                    <span>Replies within 1 business day · Mon–Fri</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 text-blaze" />
                    <span>Based in Texas, USA</span>
                  </div>
                  <a
                    href="https://instagram.com/coyotecreekhatco"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-primary hover:text-blaze transition-colors"
                  >
                    <Instagram className="h-4 w-4 text-blaze" />
                    <span>@coyotecreekhatco</span>
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {/* FAQ nudge */}
          <Link
            to="/faqs"
            className="group mt-10 flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-6 transition-colors hover:border-blaze"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blaze/10 text-blaze">
                <Search className="h-5 w-5" />
              </div>
              <div>
                <div className="font-heading text-lg text-primary">Looking for a quick answer?</div>
                <div className="text-sm text-muted-foreground">Sizing, shipping, custom orders — it's probably in the FAQs.</div>
              </div>
            </div>
            <div className="flex items-center gap-2 font-ui text-sm uppercase tracking-widest text-blaze">
              Visit FAQs
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>
      </section>

      {/* Follow Along */}
      <section className="pb-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="border-t border-primary/15 pt-16 text-center">
            <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
              Follow Along
            </span>
            <h2 className="mt-4 font-heading text-3xl font-black uppercase tracking-tight text-primary md:text-5xl">
              @coyotecreekhatco
            </h2>
            <div className="mx-auto mt-6 h-1 w-12" style={{ backgroundColor: "#FA5021" }} />
            <p className="mx-auto mt-6 max-w-md font-body text-base leading-relaxed text-primary/70 md:text-lg">
              From the field to the fairway — see where our hats have been.
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href="https://instagram.com/coyotecreekhatco"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cc-accent tracking-[0.2em]"
              >
                Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
